import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './VerifiedOrganizations.css';
import OrganizationCard from './OrganizationCard';
import { API_BASE_URL, resolveMediaUrl } from '../../utils/network.js';
import { localizeMessage } from '../../utils/toast.js';

const VerifiedOrganizations = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const getImageUrl = (imagePath) => {
    return resolveMediaUrl(imagePath, '/nkarner/default-avatar.jpg');
  };

  useEffect(() => {
    fetchVerifiedOrgs();
  }, []);

  const fetchVerifiedOrgs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      // ✅ Use the admin endpoint which is the source of truth for verified orgs
      const response = await fetch(
        `${API_BASE_URL}/admin/verified-organizations`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // Fallback: try the public endpoint
        const publicResponse = await fetch(
          `${API_BASE_URL}/organizations/verified`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
          }
        );
        if (!publicResponse.ok) throw new Error(`HTTP error! status: ${publicResponse.status}`);
        const publicData = await publicResponse.json();
        processOrgs(publicData.organizations || publicData || []);
        return;
      }

      const data = await response.json();
      if (data.success) {
        processOrgs(data.organizations || []);
      } else {
        throw new Error(data.error || 'Failed to fetch');
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(t('pages.organizations.loadError') || 'Failed to load organizations. Please try again.');
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const processOrgs = (orgsArray) => {
    // ✅ Filter: only organization accounts (not regular users)
    const orgOnly = orgsArray.filter(org => {
      const accountType = String(org?.accountType || '').toLowerCase();
      const userType    = String(org?.userType    || '').toLowerCase();
      const role        = String(org?.role        || '').toLowerCase();
      const isApproved  = String(org?.isApproved || '').toLowerCase() === 'approved';
      const isVerifiedStatus = String(org?.verificationStatus || '').toLowerCase() === 'verified';
      const isOrganizationAccount =
        accountType === 'organization' ||
        userType    === 'organizer'    ||
        role        === 'organizer'    ||
        !!org?.organizationData        ||
        !!org?.organizationName;

      return isOrganizationAccount && (isApproved || isVerifiedStatus);
    });

    const formattedOrgs = orgOnly.map((org, index) => {
      const avatarImage = org.profileImage || org.avatar;
      const avatarUrl   = getImageUrl(avatarImage);
      const about       = org.about || org.bio || org.description || 'No description available';
      const shortBio    = about.length > 100 ? about.substring(0, 100) + '...' : about;

      return {
        id:                 org.id || org._id,
        _id:                org._id,
        name:               org.organizationName || org.name || 'Unknown Organization',
        avatar:             avatarUrl,
        about,
        shortBio,
        profile_url:        `/profile/${org.id || org._id}`,
        category:           org.category || 'General',
        website:            org.website,
        contactEmail:       org.contactEmail || org.email,
        contactPhone:       org.contactPhone,
        location:           org.location,
        verificationStatus: org.verificationStatus || (org.isApproved === 'approved' ? 'verified' : 'pending'),
        isVerified:         org.isApproved === 'approved' || String(org.verificationStatus || '').toLowerCase() === 'verified',
        has_bio:            !!(org.about || org.bio || org.description),
        followersCount:     org.followersCount || 0,
        followingCount:     org.followingCount || 0,
        organizationLogo:   getImageUrl(org.organizationLogo),
        organizationPhoto:  getImageUrl(org.organizationPhoto),
        userType:           org.userType,
        accountType:        org.accountType,
        role:               org.role,
        createdAt:          org.createdAt,
        rank:               index + 1,
        postsCount:         org.postsCount || 0,
      };
    });

    setOrganizations(formattedOrgs);
  };

  const handleRefresh = () => {
    fetchVerifiedOrgs();
  };

  // ── Top 3 from actual verified orgs (or placeholders if fewer than 3) ──
  const top3 = organizations.slice(0, 3);
  while (top3.length < 3) {
    top3.push(null); // placeholder
  }

  const rankLabels = [
    t('pages.organizations.firstPlace')  || '1st Place',
    t('pages.organizations.secondPlace') || '2nd Place',
    t('pages.organizations.thirdPlace')  || '3rd Place',
  ];
  const rankDescs = [
    t('pages.organizations.firstDesc')  || '',
    t('pages.organizations.secondDesc') || '',
    t('pages.organizations.thirdDesc')  || '',
  ];

  const DEFAULT_AVATAR =
    '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';

  // Display order for the podium: 2nd | 1st | 3rd
  const podiumOrder = [1, 0, 2]; // indices into top3

  return (
    <div className="vop-page">

      {/* ── Top 3 section ── */}
      <div className="vop-top-three-section">
        <div className="vop-section-title">
          <h2>{t('pages.organizations.topTitle') || 'Top Organizations'}</h2>
          <p>{t('pages.organizations.topSubtitle') || 'Our most active verified organizations'}</p>
        </div>

        <div className="vop-top-three-grid">
          {podiumOrder.map((orgIdx, podiumPos) => {
            // podiumPos 0 = left (2nd), 1 = center (1st), 2 = right (3rd)
            const rankPos  = podiumPos === 0 ? 1 : podiumPos === 1 ? 0 : 2; // visual rank (0-indexed)
            const cardRank = rankPos + 1; // 1, 2, 3
            const org      = top3[orgIdx];

            const cardClass =
              cardRank === 1 ? 'vop-top-card vop-top-card-first' :
              cardRank === 2 ? 'vop-top-card vop-top-card-second' :
                               'vop-top-card vop-top-card-third';

            const imgSrc = org
              ? (org.avatar || DEFAULT_AVATAR)
              : '/nkarner/20260114_2034_Image Generation_remix_01keynkgbsfqt828gpcptc9qvf.png';

            return (
              <div
                key={cardRank}
                className={cardClass}
                style={{ cursor: org ? 'pointer' : 'default' }}
                onClick={() => org && navigate(`/profile/${org.id || org._id}`)}
              >
                <div className="vop-top-rank-circle">{cardRank}</div>
                <div className="vop-top-image-container">
                  <img
                    src={imgSrc}
                    alt={org ? org.name : `Top Organization ${cardRank}`}
                    className="vop-top-org-image"
                    onError={e => { e.target.onerror = null; e.target.src = DEFAULT_AVATAR; }}
                  />
                </div>
                <div className="vop-rating-stars">★ ★ ★ ★ ★</div>
                <div className="vop-top-card-info">
                  <h3>{org ? org.name : rankLabels[rankPos]}</h3>
                  <p>{org ? org.shortBio : rankDescs[rankPos]}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── All verified organizations ── */}
      <div className="vop-all-organizations-section">
        <h2 className="vop-section-title">
          {t('pages.organizations.allVerified') || 'All Verified Organizations'}
        </h2>

        {loading ? (
          <div className="vop-organizations-loading">
            <div className="vop-loading-text">
              {t('pages.organizations.loading') || 'Loading…'}
            </div>
            <div className="vop-loading-cards">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="vop-loading-card">
                  <div className="vop-loading-avatar"></div>
                  <div className="vop-loading-name"></div>
                  <div className="vop-loading-bio"></div>
                  <div className="vop-loading-stats"></div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="vop-error-message">
            <h3>{localizeMessage(error)}</h3>
            <button onClick={handleRefresh} className="vop-btn-refresh">
              {t('pages.organizations.tryAgain') || 'Try Again'}
            </button>
          </div>
        ) : organizations.length === 0 ? (
          <div className="vop-no-organizations">
            <div className="vop-empty-state">
              <div className="vop-empty-icon"></div>
              <h3>{t('pages.organizations.noneTitle') || 'No Organizations Found'}</h3>
              <p>{t('pages.organizations.noneDesc') || 'There are currently no verified organizations to display.'}</p>
              <button onClick={handleRefresh} className="vop-btn-refresh">
                {t('pages.organizations.refresh') || 'Refresh'}
              </button>
            </div>
          </div>
        ) : (
          <div className="vop-organizations-grid">
            {organizations.map(org => (
              <OrganizationCard key={org.id} organization={org} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifiedOrganizations;
